/**
 * OtherProfile — another member's profile, gated by their profile-view privacy.
 * The card, tabs and post list are the same components MyProfile uses; only the
 * header actions and the overflow menu differ.
 */
import { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useProfile } from '@wlb/shared/context/ProfileContext';
import { useFeed } from '@wlb/shared/context/FeedContext';
import { useTranslation } from '@wlb/shared/i18n/useTranslation';
import useOtherUserProfile from '@wlb/shared/hooks/useOtherUserProfile';
import useIncomingBuddyRequest from '@wlb/shared/hooks/useIncomingBuddyRequest';
import profileService from '@wlb/shared/api/services/profileService';
import { buildShareProfileUrl } from '@wlb/shared/config/env';
import { copyWithToast } from '@wlb/shared/utils/clipboardHelper';
import { canViewField } from '@wlb/shared/utils/profileVisibility';
import { ToastService } from '@wlb/shared/platform/notifier';
import {
  INVITE_STATUS,
  isAlreadyHandledInvite,
} from '@wlb/shared/utils/buddyInvite';
import {
  notifyBuddyRelationshipChanged,
  subscribeToBuddyRelationshipChanges,
} from '@wlb/shared/utils/buddyRelationshipEvents';
import {
  isFollowRelationshipTarget,
  notifyFollowRelationshipChanged,
  subscribeToFollowRelationshipChanges,
} from '@wlb/shared/utils/followRelationshipEvents';
import { Button } from '../components/ui/Button';
import { Tabs } from '../components/ui/Tabs';
import { PopoverMenu } from '../components/ui/PopoverMenu';
import { ChevronLeftIcon, PostMenuIcon } from '../components/ui/icons';
import { getProfileAvatarUrl } from '../features/profile/useCurrentProfile';
import { useProfileTabs } from '../features/profile/useProfileTabs';
import { ProfileInfoCard } from '../features/profile/ProfileInfoCard';
import { ProfileCardSkeleton } from '../features/profile/ProfileSkeletons';
import { ProfilePostList } from '../features/profile/ProfilePostList';
import { ProfileRestricted } from '../features/profile/ProfileRestricted';
import { ProfilePhotoModal } from '../features/profile/ProfilePhotoModal';
import { EMPTY_MESSAGE_KEYS } from '../features/profile/profileTabContent';
import { buildProfileMenu, type ProfileMenuKey } from '../features/profile/profileMenu';
import {
  forgetBuddyRequest,
  hasPendingBuddyRequest,
  rememberBuddyRequest,
} from '../features/profile/sentBuddyRequests';
import {
  BUDDY_RELATION_ACTION,
  resolveBuddyRelationshipState,
  type BuddyRelationshipState,
} from '../features/profile/buddyRelationshipState';
import { ReportUserModal } from '../features/profile/ReportUserModal';
import { BlockUserModal } from '../features/feed/BlockUserModal';
import { ConfirmModal } from '../features/feed/ConfirmModal';
import { useOpenDirectConversation } from '../features/messages/useOpenDirectConversation';
import useChooseAiBuddy from '@wlb/shared/hooks/useChooseAiBuddy';
import useAiBuddies from '@wlb/shared/hooks/useAiBuddies';
import { getUsernameDisplay } from '../utils/usernameDisplay';
import '../features/profile/profile.css';

/**
 * In-app relationship changes arrive instantly over buddy/follow events, so the
 * timer only has to catch an action taken in another browser or account. A slow
 * tick keeps that safety net without hammering GetOtherUserProfile.
 */
const RELATIONSHIP_POLL_INTERVAL_MS = 60000;

/** `focus` and `visibilitychange` both fire when a tab comes back. */
const MIN_REFRESH_GAP_MS = 5000;

export function OtherProfile() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { userGuid, userId } = useParams<{ userGuid?: string; userId?: string }>();
  const targetGuid = userGuid || userId;
  const { state } = useLocation();
  const isBuddyFlow = Boolean((state as any)?.isBuddyFlow);
  const { profile: viewerProfile, userRelation, updateInvite } = useProfile();
  const { refreshFeed } = useFeed();
  const { profile, loading, error, restriction, refetch } = useOtherUserProfile(targetGuid);
  const { openConversation, openingUserId } = useOpenDirectConversation();
  const { items, activeKey, setActiveKey, action, posts } = useProfileTabs(
    restriction ? undefined : profile?.userId,
    false,
    restriction ? undefined : profile?.userGuid ?? targetGuid,
  );

  // Own profile → My Profile
  useEffect(() => {
    if (
      targetGuid &&
      viewerProfile?.userGuid &&
      targetGuid.toLowerCase() === viewerProfile.userGuid.toLowerCase()
    ) {
      navigate('/profile', { replace: true });
    }
  }, [targetGuid, viewerProfile?.userGuid, navigate]);
  const [menuAnchorRect, setMenuAnchorRect] = useState<DOMRect | null>(null);
  // Two triggers share one menu, so only the one that opened it reports expanded.
  const [menuSource, setMenuSource] = useState<'topbar' | 'buddy' | null>(null);
  const [blockOpen, setBlockOpen] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);
  const [relationLoading, setRelationLoading] = useState(false);
  const [relationshipOverride, setRelationshipOverride] =
    useState<BuddyRelationshipState | null>(null);
  const [removeBuddyOpen, setRemoveBuddyOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [photoViewerOpen, setPhotoViewerOpen] = useState(false);
  const [followPrompt, setFollowPrompt] = useState<'follow' | 'unfollow' | null>(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const { buddies } = useAiBuddies();
  const { choose } = useChooseAiBuddy();

  const aiBuddy = useMemo(() => {
    if (!profile) return null;
    const profileGuid = (profile.userGuid ?? userGuid ?? '').toLowerCase();
    return buddies.find(b => 
      (b.profileUserGuid ?? '').toLowerCase() === profileGuid || 
      (b.agentGuid ?? '').toLowerCase() === profileGuid
    ) ?? null;
  }, [profile, userGuid, buddies]);

  const isAIBuddy = Boolean(aiBuddy);

  const locallySentRequest = hasPendingBuddyRequest(
    profile?.userId,
    viewerProfile?.userId,
  );
  const pendingStatus = profile?.status?.trim().toLowerCase() === 'pending';
  const shouldCheckIncomingRequest =
    Boolean(profile?.userId) &&
    !profile?.isBuddy &&
    !profile?.isAccepted &&
    (
      Boolean(profile?.invited) ||
      Boolean(profile?.isRequested) ||
      locallySentRequest ||
      pendingStatus
    );
  const {
    incoming: incomingRequest,
    loading: incomingRequestLoading,
  } = useIncomingBuddyRequest(
    viewerProfile?.userId,
    profile?.userId,
    shouldCheckIncomingRequest,
  );

  const relationshipState = resolveBuddyRelationshipState({
    isBuddy: Boolean(profile?.isBuddy),
    isAccepted: Boolean(profile?.isAccepted),
    invited: Boolean(profile?.invited),
    isRequested: Boolean(profile?.isRequested),
    status: profile?.status,
    hasLocallySentRequest: locallySentRequest,
    incomingLookup: incomingRequest,
    checkingIncoming: incomingRequestLoading,
    override: relationshipOverride,
  });
  const isBuddy = relationshipState === 'accepted';
  const profileUsername = getUsernameDisplay(profile, 'user');

  // Following is tracked locally so the menu label flips straight after the
  // call, and re-syncs whenever a fresh profile lands.
  useEffect(() => {
    setIsFollowing(Boolean(profile?.isFollowing));
  }, [profile?.isFollowing]);

  useEffect(() => {
    const subscription = subscribeToFollowRelationshipChanges(change => {
      if (isFollowRelationshipTarget(change, {
        userId: profile?.userId,
        profileUserGuid: profile?.userGuid ?? targetGuid,
      })) {
        setIsFollowing(change.isFollowing);
      }
    });
    return () => subscription.remove();
  }, [profile?.userGuid, profile?.userId, targetGuid]);

  // An accepted request is no longer pending, so the hint must not outlive it.
  useEffect(() => {
    if (isBuddy) {
      forgetBuddyRequest(profile?.userId, viewerProfile?.userId);
    }
  }, [isBuddy, profile?.userId, viewerProfile?.userId]);

  useEffect(() => {
    setRelationshipOverride(null);
  }, [profile?.userId]);

  // Actions from Notifications and Connections update an already-mounted
  // profile immediately. A short visible-page refresh also catches actions
  // taken by the other member in another browser/account.
  useEffect(() => {
    const targetUserId = Number(profile?.userId);
    if (!targetUserId) return undefined;

    const subscription = subscribeToBuddyRelationshipChanges(change => {
      if (change.relatedUserId !== targetUserId) return;
      if (change.action === 'accept') {
        setRelationshipOverride('accepted');
      } else if (change.action === 'request') {
        setRelationshipOverride('outgoing');
      } else {
        setRelationshipOverride('none');
      }
      refetch();
    });

    let lastRefreshAt = Date.now();
    let interval: number | null = null;

    const stopPolling = (): void => {
      if (interval !== null) {
        window.clearInterval(interval);
        interval = null;
      }
    };

    const startPolling = (): void => {
      stopPolling();
      interval = window.setInterval(() => {
        lastRefreshAt = Date.now();
        refetch({ silent: true });
      }, RELATIONSHIP_POLL_INTERVAL_MS);
    };

    const refreshWhenVisible = (): void => {
      if (document.visibilityState !== 'visible') {
        stopPolling();
        return;
      }
      if (Date.now() - lastRefreshAt >= MIN_REFRESH_GAP_MS) {
        lastRefreshAt = Date.now();
        refetch({ silent: true });
      }
      startPolling();
    };

    if (document.visibilityState === 'visible') startPolling();
    window.addEventListener('focus', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);

    return () => {
      subscription.remove();
      stopPolling();
      window.removeEventListener('focus', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [profile?.userId, refetch]);

  const openMenu = (event: { currentTarget: HTMLElement }, source: 'topbar' | 'buddy') => {
    setMenuAnchorRect(event.currentTarget.getBoundingClientRect());
    setMenuSource(source);
  };

  const closeMenu = () => {
    setMenuAnchorRect(null);
    setMenuSource(null);
  };

  const handleMenuSelect = (key: ProfileMenuKey) => {
    closeMenu();
    const shareTargetId =
      profile?.userGuid ||
      userGuid ||
      profile?.id ||
      (profile?.userId ? String(profile.userId) : '') ||
      profileUsername.toLowerCase() ||
      '';
    const shareUrl = buildShareProfileUrl(shareTargetId);

    switch (key) {
      case 'copyLink':
        copyWithToast(shareUrl, t('profile.toast.copied'));
        break;
      case 'share':
        if (navigator.share) {
          const username = profileUsername;
          const bio = profile?.bio?.trim();
          const shareText = bio
            ? t('profile.shareProfile.messageWithBio', { username, bio })
            : t('profile.shareProfile.message', { username });
          navigator.share({ title: profileUsername, text: shareText, url: shareUrl }).catch(() => undefined);
        } else {
          copyWithToast(shareUrl, t('profile.toast.copied'));
        }
        break;
      case 'report':
        setReportOpen(true);
        break;
      case 'block':
        setBlockOpen(true);
        break;
      case 'removeBuddy':
        setRemoveBuddyOpen(true);
        break;
      case 'toggleFollow':
        setFollowPrompt(isFollowing ? 'unfollow' : 'follow');
        break;
      default:
        break;
    }
  };

  const handleSendBuddyRequest = async () => {
    const relatedUserId = Number(profile?.userId);
    if (!relatedUserId) return;
    setRelationLoading(true);
    try {
      const result = await userRelation(
        relatedUserId,
        BUDDY_RELATION_ACTION.request,
      );
      if (result?.success === false) {
        throw new Error(result.message || 'Buddy request failed');
      }
      rememberBuddyRequest(relatedUserId, viewerProfile?.userId);
      setRelationshipOverride('outgoing');
      notifyBuddyRelationshipChanged({
        relatedUserId,
        action: 'request',
      });
      ToastService.show({ type: 'success', message: t('profile.toast.requestSent') });
    } catch {
      ToastService.show({ type: 'error', message: t('common.errors.generic') });
    } finally {
      setRelationLoading(false);
    }
  };

  const handleCancelBuddyRequest = async () => {
    const recipientId = Number(profile?.userId);
    if (!recipientId) return;
    setRelationLoading(true);
    try {
      await updateInvite(recipientId, INVITE_STATUS.cancel);
      forgetBuddyRequest(recipientId, viewerProfile?.userId);
      setRelationshipOverride('none');
      notifyBuddyRelationshipChanged({
        relatedUserId: recipientId,
        action: 'cancel',
      });
      refetch();
      ToastService.show({
        type: 'success',
        message: t('profile.toast.requestCancelled'),
      });
    } catch {
      ToastService.show({
        type: 'error',
        message: t('common.errors.generic'),
      });
    } finally {
      setRelationLoading(false);
    }
  };

  const handleIncomingBuddyRequest = async (accept: boolean) => {
    const senderId = Number(profile?.userId);
    if (!senderId) return;
    setRelationLoading(true);
    try {
      await updateInvite(
        senderId,
        accept ? INVITE_STATUS.accept : INVITE_STATUS.reject,
      );
    } catch (caught) {
      if (!isAlreadyHandledInvite(caught)) {
        ToastService.show({
          type: 'error',
          message: t('common.errors.generic'),
        });
        setRelationLoading(false);
        return;
      }
    }

    forgetBuddyRequest(senderId, viewerProfile?.userId);
    setRelationshipOverride(accept ? 'accepted' : 'none');
    notifyBuddyRelationshipChanged({
      relatedUserId: senderId,
      action: accept ? 'accept' : 'reject',
    });
    refetch();
    ToastService.show({
      type: 'success',
      message: t(
        accept
          ? 'home.notifications.toasts.buddyAccepted'
          : 'home.notifications.toasts.buddyRejected',
      ),
    });
    setRelationLoading(false);
  };

  const handleConfirmRemoveBuddy = async () => {
    const relatedUserId = Number(profile?.userId);
    if (!relatedUserId) return;
    setRelationLoading(true);
    try {
      const result = await userRelation(
        relatedUserId,
        BUDDY_RELATION_ACTION.removeBuddy,
      );
      if (result?.success === false) {
        throw new Error(result.message || 'Buddy removal failed');
      }
      forgetBuddyRequest(relatedUserId, viewerProfile?.userId);
      setRelationshipOverride('none');
      notifyBuddyRelationshipChanged({
        relatedUserId,
        action: 'remove',
      });
      setRemoveBuddyOpen(false);
      refetch();
      ToastService.show({ type: 'success', message: t('profile.toast.buddyRemoved') });
    } catch {
      ToastService.show({ type: 'error', message: t('common.errors.generic') });
    } finally {
      setRelationLoading(false);
    }
  };

  const handleConfirmFollowToggle = async () => {
    const relatedUserId = Number(profile?.userId);
    if (!relatedUserId || !followPrompt) return;
    const nextFollowing = followPrompt === 'follow';
    const previousFollowing = isFollowing;
    setIsFollowing(nextFollowing);
    setFollowPrompt(null);
    setRelationLoading(true);
    try {
      const result = await userRelation(
        relatedUserId,
        nextFollowing ? 'Follow' : 'UnFollow',
      );
      if (result?.success === false) {
        throw new Error(result.message || 'Follow relation update failed');
      }
      const confirmedFollowing =
        typeof result?.isFollowing === 'boolean'
          ? result.isFollowing
          : nextFollowing;
      setIsFollowing(confirmedFollowing);
      notifyFollowRelationshipChanged({
        relatedUserId,
        profileUserGuid: profile?.userGuid ?? targetGuid,
        isFollowing: confirmedFollowing,
      });
      ToastService.show({
        type: 'success',
        message: t(nextFollowing ? 'profile.toast.followed' : 'profile.toast.unfollowed'),
      });
    } catch {
      setIsFollowing(previousFollowing);
      ToastService.show({ type: 'error', message: t('common.errors.generic') });
    } finally {
      setRelationLoading(false);
    }
  };

  const handleConfirmBlock = async () => {
    if (!profile?.userId) return;
    setBlockLoading(true);
    const result = await profileService.setBlockRelation(profile.userId, true);
    setBlockLoading(false);
    if (!result.success) {
      ToastService.show({ type: 'error', message: result.message || t('common.errors.generic') });
      return;
    }
    setBlockOpen(false);
    ToastService.show({ type: 'success', message: t('profile.toast.blocked') });
    navigate('/');
    refreshFeed().catch(() => undefined);
  };

  const handleMessage = () => {
    // The direct thread caches whatever avatar it is opened with, so apply the
    // owner's profile-picture privacy first — an inaccessible response may
    // still carry a picture this viewer is not allowed to see.
    const avatar =
      profile && canViewField(profile.profilePicturePrivacy, { isBuddy })
        ? getProfileAvatarUrl(profile)
        : '';
    openConversation({
      userId: Number(profile?.userId),
      name: profileUsername,
      avatar,
    }).catch(() => undefined);
  };

  const openingConversation = openingUserId != null && openingUserId === String(profile?.userId ?? '');

  /**
   * Facebook-style actions for an inaccessible profile: Add Buddy (or the
   * pending "Requested" state) plus Chat. Follow/Unfollow is deliberately
   * absent — it takes effect immediately, without the owner's approval.
   */
  const renderRestrictedActions = () => {
    const chatButton = (
      <Button
        variant="ghost"
        onClick={handleMessage}
        disabled={openingConversation}
        aria-busy={openingConversation}
      >
        {t(openingConversation ? 'chat.opening' : 'profile.buttons.message')}
      </Button>
    );

    if (relationshipState === 'accepted') {
      return chatButton;
    }

    if (relationshipState === 'incoming') {
      return (
        <>
          <Button
            onClick={() => {
              handleIncomingBuddyRequest(true).catch(() => undefined);
            }}
            disabled={relationLoading}
            aria-busy={relationLoading}
          >
            {t('connections.request.accept')}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              handleIncomingBuddyRequest(false).catch(() => undefined);
            }}
            disabled={relationLoading}
            aria-busy={relationLoading}
          >
            {t('connections.request.reject')}
          </Button>
        </>
      );
    }

    if (relationshipState === 'checking') {
      return (
        <>
          <Button disabled aria-busy>
            {t('common.loading')}
          </Button>
          {chatButton}
        </>
      );
    }

    if (relationshipState === 'outgoing') {
      return (
        <>
          <Button
            variant="outline"
            onClick={() => {
              handleCancelBuddyRequest().catch(() => undefined);
            }}
            disabled={relationLoading}
            aria-busy={relationLoading}
          >
            {t(
              relationLoading
                ? 'profile.buttons.cancellingRequest'
                : 'profile.buttons.cancelRequest',
            )}
          </Button>
          <Button
            onClick={handleMessage}
            disabled={openingConversation}
            aria-busy={openingConversation}
          >
            {t(openingConversation ? 'chat.opening' : 'profile.buttons.message')}
          </Button>
        </>
      );
    }

    return (
      <>
        <Button
          onClick={() => { handleSendBuddyRequest().catch(() => undefined); }}
          disabled={relationLoading}
        >
          {t('profile.buttons.requestBuddy')}
        </Button>
        {chatButton}
      </>
    );
  };

  const renderActions = () => {
    if (isAIBuddy) {
      if (isFollowing) {
        return (
          <>
            <Button
              onClick={() => navigate('/robi')}
            >
              {t('aiBuddy.details.chatBuddyButton', 'Chat')}
            </Button>
            <Button
              variant="outline"
              onClick={() => { if (aiBuddy) choose(aiBuddy).catch(() => undefined); }}
              disabled={relationLoading}
            >
              {t('profile.buttons.unfollow')}
            </Button>
          </>
        );
      }

      return (
        <Button
          onClick={() => { if (aiBuddy) choose(aiBuddy).catch(() => undefined); }}
          disabled={relationLoading}
        >
          {t('aiBuddy.details.chooseButton')}
        </Button>
      );
    }

    if (relationshipState === 'accepted') {
      return (
        <Button
          onClick={handleMessage}
          disabled={openingConversation}
          aria-busy={openingConversation}
          fullWidth
        >
          {t(openingConversation ? 'chat.opening' : 'aiBuddy.details.chatBuddyButton', 'Chat')}
        </Button>
      );
    }

    if (relationshipState === 'incoming') {
      return (
        <>
          <Button
            onClick={() => {
              handleIncomingBuddyRequest(true).catch(() => undefined);
            }}
            disabled={relationLoading}
            aria-busy={relationLoading}
          >
            {t('connections.request.accept')}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              handleIncomingBuddyRequest(false).catch(() => undefined);
            }}
            disabled={relationLoading}
            aria-busy={relationLoading}
          >
            {t('connections.request.reject')}
          </Button>
        </>
      );
    }

    if (relationshipState === 'checking') {
      return (
        <>
          <Button disabled aria-busy>
            {t('common.loading')}
          </Button>
          <Button
            onClick={handleMessage}
            disabled={openingConversation}
            aria-busy={openingConversation}
          >
            {t(openingConversation ? 'chat.opening' : 'profile.buttons.message')}
          </Button>
        </>
      );
    }

    if (relationshipState === 'outgoing') {
      return (
        <>
          <Button
            variant="outline"
            onClick={() => {
              handleCancelBuddyRequest().catch(() => undefined);
            }}
            disabled={relationLoading}
            aria-busy={relationLoading}
          >
            {t(
              relationLoading
                ? 'profile.buttons.cancellingRequest'
                : 'profile.buttons.cancelRequest',
            )}
          </Button>
          <Button
            onClick={handleMessage}
            disabled={openingConversation}
            aria-busy={openingConversation}
          >
            {t(openingConversation ? 'chat.opening' : 'profile.buttons.message')}
          </Button>
        </>
      );
    }

    // null / empty / declined
    if (isBuddyFlow) {
      return (
        <Button
          onClick={() => { handleSendBuddyRequest().catch(() => undefined); }}
          disabled={relationLoading}
        >
          {t('profile.buttons.requestBuddy')}
        </Button>
      );
    }

    return (
      <>
        <Button
          onClick={() => { handleSendBuddyRequest().catch(() => undefined); }}
          disabled={relationLoading}
        >
          {t('profile.buttons.requestBuddy')}
        </Button>
        <Button
          variant="ghost"
          onClick={handleMessage}
          disabled={openingConversation}
          aria-busy={openingConversation}
        >
          {t(openingConversation ? 'chat.opening' : 'profile.buttons.message')}
        </Button>
      </>
    );
  };

  const renderBody = () => {
    if (loading) {
      return <ProfileCardSkeleton />;
    }

    if (restriction) {
      const showActions =
        profile?.userId && restriction !== 'unavailable';
      // The locked view keeps the normal tab strip
      // (All/Posts/Photos/Videos); every tab's content stays locked.
      const showTabs = Boolean(profile) && restriction !== 'unavailable';
      return (
        <ProfileRestricted
          reason={restriction}
          profile={profile}
          actions={showActions ? renderRestrictedActions() : undefined}
          tabs={
            showTabs ? (
              <Tabs
                items={items}
                activeKey={activeKey}
                onChange={setActiveKey}
                label={t('profile.accessibility.profileTabs')}
                idPrefix="profile-tab"
              />
            ) : undefined
          }
          activeTabKey={showTabs ? activeKey : undefined}
        />
      );
    }

    if (error || !profile) {
      return (
        <div className="profile-posts__status profile-posts__status--error" role="alert">
          <p>{error || t('profile.toast.unableToLoadProfile')}</p>
          <Button size="sm" onClick={() => refetch()}>
            {t('common.buttons.retry')}
          </Button>
        </div>
      );
    }

    return (
      <>
        <ProfileInfoCard
          profile={profile}
          viewer={{ isBuddy }}
          onViewPhoto={() => setPhotoViewerOpen(true)}
          onSelectStat={key => navigate(`/profile/others/${userGuid}/connections?tab=${key}`)}
          actions={renderActions()}
        />

        <Tabs
          items={items}
          activeKey={activeKey}
          onChange={setActiveKey}
          label={t('profile.accessibility.profileTabs')}
          idPrefix="profile-tab"
        />

        <div
          role="tabpanel"
          id={`profile-tab-panel-${activeKey}`}
          aria-labelledby={`profile-tab-${activeKey}`}
        >
          {action ? (
            <ProfilePostList
              posts={posts}
              viewerProfile={viewerProfile}
              emptyMessage={
                activeKey === 'posts' || activeKey === 'all'
                  ? t('profile.empty.otherPosts', { name: profileUsername })
                  : t(EMPTY_MESSAGE_KEYS[activeKey])
              }
            />
          ) : (
            <p className="profile-posts__empty">{t('profile.empty.activities')}</p>
          )}
        </div>
      </>
    );
  };

  return (
    <section className="profile-page" aria-label={t('profile.header.title')}>
      <div className="profile-topbar">
        <button
          type="button"
          className="profile-topbar__back"
          aria-label={t('profile.accessibility.back')}
          onClick={() => navigate(-1)}
        >
          <ChevronLeftIcon size={20} />
        </button>
        <h1 className="profile-topbar__title">{t('profile.header.title')}</h1>
        <button
          type="button"
          className="profile-topbar__button"
          aria-label={t('profile.accessibility.openMenu')}
          aria-haspopup="dialog"
          aria-expanded={menuSource === 'topbar'}
          disabled={!profile}
          onClick={event => openMenu(event, 'topbar')}
        >
          <PostMenuIcon size={20} />
        </button>
      </div>

      {renderBody()}

      {photoViewerOpen && profile && (
        <ProfilePhotoModal
          profile={profile}
          onClose={() => setPhotoViewerOpen(false)}
        />
      )}

      <PopoverMenu
        anchorRect={menuAnchorRect}
        items={buildProfileMenu({ isBlocked: false, isBuddy, isFollowing, canFollow: !restriction }, t)}
        label={t('profile.accessibility.menu')}
        labelledBy="profile-menu-title"
        onClose={closeMenu}
        onSelect={handleMenuSelect}
      />

      {reportOpen && (profile?.userGuid || userGuid) && (
        <ReportUserModal
          userGuid={String(profile?.userGuid ?? userGuid)}
          onClose={() => setReportOpen(false)}
        />
      )}

      {followPrompt && profile && (
        <ConfirmModal
          title={t(`profile.${followPrompt}.title`, { name: profileUsername })}
          message={t(`profile.${followPrompt}.description`, { name: profileUsername })}
          confirmLabel={t(`profile.${followPrompt}.confirm`)}
          cancelLabel={t('common.buttons.cancel')}
          mediaUrl={getProfileAvatarUrl(profile)}
          mediaFit="contain"
          loading={relationLoading}
          onClose={() => setFollowPrompt(null)}
          onConfirm={() => { handleConfirmFollowToggle().catch(() => undefined); }}
        />
      )}

      {removeBuddyOpen && profile && (
        <ConfirmModal
          title={t('connections.removeBuddy.title', { name: profileUsername })}
          message={t('connections.removeBuddy.description', { name: profileUsername })}
          confirmLabel={t('connections.removeBuddy.confirm')}
          cancelLabel={t('common.buttons.cancel')}
          mediaUrl={getProfileAvatarUrl(profile)}
          mediaFit="contain"
          loading={relationLoading}
          onClose={() => setRemoveBuddyOpen(false)}
          onConfirm={() => { handleConfirmRemoveBuddy().catch(() => undefined); }}
        />
      )}

      {blockOpen && profile && (
        <BlockUserModal
          username={profileUsername}
          avatarUrl={getProfileAvatarUrl(profile)}
          seed={profile.userGuid ?? profile.userId}
          loading={blockLoading}
          onClose={() => setBlockOpen(false)}
          onConfirm={() => { handleConfirmBlock().catch(() => undefined); }}
        />
      )}
    </section>
  );
}
